import { toastManager } from "@salestrak/ui";
import { isForbiddenError } from "#app/lib/utils/is-forbidden-error";

// Standard catch-block toast for GraphQL mutations. Skips FORBIDDEN-coded
// errors — the Apollo error link in apollo-client.ts already shows the
// centralized "no access" toast for those, and toasting here too would
// double-toast every permission denial.
// A refusal whose server message is written for a person (a Project in the
// trash) shows that sentence instead of the fallback, so the user learns why.
export function toastMutationError(err: unknown, fallback: string): void {
  if (isForbiddenError(err)) return;
  const title = hasErrorCode(err, WORDED_CODES) ? graphQLErrorMessage(err) : undefined;
  toastManager.add({ title: title ?? fallback, type: "error" });
}

const WORDED_CODES = new Set(["ISSUE_TRASHED"]);

function hasErrorCode(err: unknown, codes: ReadonlySet<string>): boolean {
  if (!err || typeof err !== "object") return false;
  const candidate = err as {
    errors?: ReadonlyArray<unknown>;
    graphQLErrors?: ReadonlyArray<unknown>;
  };
  const errors = candidate.graphQLErrors ?? candidate.errors;
  if (!Array.isArray(errors)) return false;
  return errors.some((e) => {
    const code = (e as { extensions?: { code?: unknown } })?.extensions?.code;
    return typeof code === "string" && codes.has(code);
  });
}

// Pulls the user-facing message off a non-success union member (ValidationError,
// NotFoundError, PermissionDenied). Returns undefined when the result is null
// or doesn't carry one — caller falls back to a generic toast. Exists because
// codegen unions keep `__typename` optional, so `!== "Success"` never narrows
// the success member away and `.message` won't typecheck directly.
export function resultMessage(result: unknown): string | undefined {
  if (!result || typeof result !== "object") return undefined;
  const msg = (result as { message?: unknown }).message;
  return typeof msg === "string" && msg.length > 0 ? msg : undefined;
}

// The message of the first GraphQL error on a thrown mutation error — the
// sentence a server guard wrote for a person ("… is the default Source"), for
// mutations whose result is not a union with a ValidationError member.
// Duck-typed on `errors` / `graphQLErrors` like `isForbiddenError`.
export function graphQLErrorMessage(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const candidate = err as {
    errors?: ReadonlyArray<unknown>;
    graphQLErrors?: ReadonlyArray<unknown>;
  };
  const errors = candidate.graphQLErrors ?? candidate.errors;
  if (!Array.isArray(errors)) return undefined;
  const msg = (errors[0] as { message?: unknown } | undefined)?.message;
  return typeof msg === "string" && msg.length > 0 ? msg : undefined;
}
